import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Link as RouterLink } from "react-router-dom";
import {
  Badge,
  Box,
  Divider,
  Flex,
  Link,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import {
  getEntityReview,
  getFieldCatalog,
  requestVerificationClarification,
  updateVerificationDecision,
} from "../../actions/verificationReview.action";
import ReviewFieldTile from "./ReviewFieldTile";
import ReviewActionBar from "./ReviewActionBar";
import { accountStatusInfo, formatDateTime, reviewStatusInfo } from "./verificationStatus";

const DETAILS_ROUTE = {
  vendor: "/verification/vendor-approvals",
  driver: "/verification/driver-approvals",
  vehicle: "/verification/vehicle-approvals",
};

const AccountSummary = ({ account }) => {
  if (!account) return null;
  const info = accountStatusInfo(account.overall?.status);
  const req = account.overall?.requirements || {};
  const sections = [
    ["Profile", account.sections?.profile || []],
    ["Drivers", account.sections?.drivers || []],
    ["Vehicles", account.sections?.vehicles || []],
  ];

  return (
    <Box border="1px solid" borderColor="gray.200" borderRadius="lg" p={4} bg="gray.50">
      <Flex justify="space-between" align="center" wrap="wrap" gap={2} mb={2}>
        <Text fontWeight="semibold" color="gray.700">
          Account verification
        </Text>
        <Flex gap={2} align="center">
          <Badge colorScheme={info.color}>{info.label}</Badge>
          <Badge colorScheme={account.overall?.canAcceptBookings ? "green" : "gray"} variant="outline">
            {account.overall?.canAcceptBookings ? "Can accept bookings" : "Cannot accept bookings"}
          </Badge>
        </Flex>
      </Flex>
      <Text fontSize="sm" color="gray.600" mb={3}>
        {account.overall?.accountType === "driver"
          ? `Driver account — Active needs its own driver review approved ${req.profileApproved ? "✓" : "✗"}`
          : `Active needs: Profile approved ${req.profileApproved ? "✓" : "✗"} · Drivers approved: ${req.approvedDrivers ?? 0} (min 1) · Vehicles approved: ${req.approvedVehicles ?? 0} (min 1)`}
      </Text>
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
        {sections.map(([title, items]) => (
          <Box key={title}>
            <Text fontSize="xs" textTransform="uppercase" color="gray.500" mb={1}>
              {title}
            </Text>
            {items.length === 0 && (
              <Text fontSize="sm" color="gray.500">
                None
              </Text>
            )}
            {items.map((item) => {
              const s = reviewStatusInfo(item.status);
              return (
                <Flex key={item.reviewId} justify="space-between" align="center" gap={2} mb={1}>
                  <Link
                    as={RouterLink}
                    to={`${DETAILS_ROUTE[item.entityType]}/${item.entityId}`}
                    state={{ row: { id: item.entityId, entityId: item.entityId, reviewId: item.reviewId } }}
                    fontSize="sm"
                    color="blue.600"
                    noOfLines={1}
                  >
                    {item.displayName || item.entityId}
                  </Link>
                  <Badge colorScheme={s.color} fontSize="0.65em">
                    {s.label}
                  </Badge>
                </Flex>
              );
            })}
          </Box>
        ))}
      </SimpleGrid>
    </Box>
  );
};

AccountSummary.propTypes = { account: PropTypes.object };

/**
 * Field-level verification for one Vendor / Driver / Vehicle:
 * shows every reviewable field, lets the admin pick fields + a reason for each,
 * shows requested / resubmitted markers, and Approve / Reject / Request Clarification.
 */
const VerificationReviewPanel = ({ entityType, entityId, onChanged }) => {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [detail, setDetail] = useState(null);
  const [clarificationMode, setClarificationMode] = useState(false);
  const [selected, setSelected] = useState({}); // fieldKey -> reason
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    if (!entityId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [catalogRes, reviewRes] = await Promise.all([getFieldCatalog(), getEntityReview(entityType, entityId)]);
      setCatalog(catalogRes?.data?.[entityType] || []);
      setDetail(reviewRes?.data || null);
    } catch (error) {
      toast({ title: "Failed to load verification review", description: error.message, status: "error", isClosable: true });
    } finally {
      setLoading(false);
    }
  }, [entityId, entityType, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const review = detail?.review;
  const requestedByKey = useMemo(
    () => Object.fromEntries((review?.requestedFields || []).map((f) => [f.fieldKey, f])),
    [review],
  );
  const resubmittedCount = (review?.requestedFields || []).filter((f) => f.isResubmitted).length;

  const finish = async (successTitle) => {
    toast({ title: successTitle, status: "success", duration: 3000, isClosable: true });
    setClarificationMode(false);
    setSelected({});
    setMessage("");
    await load();
    onChanged?.();
  };

  const handleDecision = async (decision, reason = "") => {
    setBusy(true);
    try {
      await updateVerificationDecision({ entityType, entityId, reviewId: review?.reviewId, decision, reason });
      await finish(decision === "approve" ? "Approved" : "Rejected");
      return true;
    } catch (error) {
      toast({ title: "Action failed", description: error.message, status: "error", isClosable: true });
      return false;
    } finally {
      setBusy(false);
    }
  };

  const toggleField = (field) =>
    setSelected((prev) => {
      const next = { ...prev };
      if (field.fieldKey in next) delete next[field.fieldKey];
      else next[field.fieldKey] = "";
      return next;
    });

  const sendClarification = async () => {
    const entries = Object.entries(selected);
    const missing = entries.filter(([, reason]) => !reason.trim());
    if (missing.length) {
      toast({ title: "Add a reason for every selected field", status: "warning", isClosable: true });
      return;
    }
    setBusy(true);
    try {
      await requestVerificationClarification({
        entityType,
        entityId,
        reviewId: review?.reviewId,
        message: message.trim(),
        requestedFields: entries.map(([fieldKey, reason]) => ({
          fieldKey,
          fieldLabel: catalog.find((f) => f.fieldKey === fieldKey)?.fieldLabel || fieldKey,
          reason: reason.trim(),
        })),
      });
      await finish("Clarification request sent");
    } catch (error) {
      toast({ title: "Failed to send clarification", description: error.message, status: "error", isClosable: true });
    } finally {
      setBusy(false);
    }
  };

  if (!entityId) {
    return (
      <Box bg="white" borderRadius="xl" p={6} shadow="sm">
        <Text color="gray.600">This review is not linked to a {entityType} record, so field-level review is not available.</Text>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box bg="white" borderRadius="xl" p={6} shadow="sm">
        <Spinner size="sm" mr={2} /> Loading verification review…
      </Box>
    );
  }

  if (!review) return null;

  const history = detail?.clarificationHistory || [];

  return (
    <Box bg="white" borderRadius="xl" p={6} shadow="sm">
      <Stack spacing={4}>
        <Text fontSize="xl" fontWeight="bold" color="gray.700">
          Verification Review
        </Text>

        <AccountSummary account={detail?.account} />

        <ReviewActionBar
          status={review.status}
          resubmitted={resubmittedCount > 0}
          busy={busy}
          clarificationMode={clarificationMode}
          selectedCount={Object.keys(selected).length}
          clarificationMessage={message}
          onClarificationMessageChange={setMessage}
          onApprove={() => handleDecision("approve")}
          onReject={(reason) => handleDecision("reject", reason)}
          onStartClarification={() => setClarificationMode(true)}
          onCancelClarification={() => {
            setClarificationMode(false);
            setSelected({});
            setMessage("");
          }}
          onSendClarification={sendClarification}
        />

        {review.status === "rejected" && review.rejectionReason && (
          <Text fontSize="sm" color="red.600">
            Rejection reason: {review.rejectionReason}
          </Text>
        )}
        {review.status === "clarification_requested" && review.adminMessage && (
          <Text fontSize="sm" color="orange.700">
            Message sent: {review.adminMessage} ({formatDateTime(review.clarificationRequestedAt)})
          </Text>
        )}
        {resubmittedCount > 0 && (
          <Text fontSize="sm" color="green.700">
            Vendor resubmitted {resubmittedCount} field(s) on {formatDateTime(review.resubmittedAt)}.
          </Text>
        )}

        <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={3}>
          {catalog.map((field) => (
            <ReviewFieldTile
              key={field.fieldKey}
              field={field}
              value={detail?.currentValues?.[field.fieldKey]}
              requested={requestedByKey[field.fieldKey] || null}
              selectable={clarificationMode}
              selected={field.fieldKey in selected}
              reason={selected[field.fieldKey] || ""}
              onToggle={toggleField}
              onReasonChange={(f, value) => setSelected((prev) => ({ ...prev, [f.fieldKey]: value }))}
            />
          ))}
        </SimpleGrid>

        {history.length > 0 && (
          <>
            <Divider />
            <Text fontSize="sm" fontWeight="semibold">
              Clarification history
            </Text>
            <Stack spacing={2}>
              {[...history].reverse().map((round, index) => (
                <Box key={index} border="1px solid" borderColor="gray.100" borderRadius="md" p={3}>
                  <Text fontSize="xs" color="gray.500">
                    Requested {formatDateTime(round.sentAt)}
                    {round.respondedAt ? ` · Resubmitted ${formatDateTime(round.respondedAt)}` : " · Waiting for vendor"}
                  </Text>
                  <Text fontSize="sm">{round.message}</Text>
                  {(round.requestedFields || []).map((f) => (
                    <Text key={f.fieldKey} fontSize="xs" color="gray.600">
                      • {f.fieldLabel || f.fieldKey}
                      {f.reason ? ` — ${f.reason}` : ""}
                      {f.isResubmitted ? " (resubmitted)" : ""}
                    </Text>
                  ))}
                </Box>
              ))}
            </Stack>
          </>
        )}
      </Stack>
    </Box>
  );
};

VerificationReviewPanel.propTypes = {
  entityType: PropTypes.oneOf(["vendor", "driver", "vehicle"]).isRequired,
  entityId: PropTypes.string,
  onChanged: PropTypes.func,
};

export default VerificationReviewPanel;
